import "./QuestionBoxCreators.css";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { HStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { type JSX, Match, Show, Switch } from "solid-js";

export type QuestionBoxCreator = {
	handle: string;
	displayName?: string | null;
};

/** Beyond this many, naming each creator would crowd the row. */
const MAX_NAMED_CREATORS = 3;

const creatorName = (creator: QuestionBoxCreator) =>
	creator.displayName || `@${creator.handle}`;

/**
 * The creators an Ask question goes to, in place of the Studio context row.
 * No creators means every creator the viewer subscribes to.
 */
export const QuestionBoxCreators = (props: {
	creators: QuestionBoxCreator[];
	/** Shown with "All subscriptions" when no creators are named. */
	subscriptionCount?: number;
	/** Why there is no creator to ask, shown instead of the creators. */
	notice?: string;
	/** Secondary detail, at the far end of the row. */
	detail?: string;
	/** A control after the detail, at the very end of the row. */
	trailing?: JSX.Element;
}) => {
	const count = () => props.creators.length;
	return (
		<HStack
			class="question-box-creators"
			align="center"
			spacing="small"
			justify="space-between"
		>
			<div class="question-box-creators__main">
				<div class="question-box-creators__heading">
					<Switch>
						<Match when={props.notice}>
							<MaterialSymbol
								symbol="person_off"
								color="fg4"
								relativeSize={0.85}
							/>
						</Match>
						<Match when={count() === 1}>
							<MaterialSymbol
								symbol="video_library"
								color="primary"
								relativeSize={0.85}
							/>
						</Match>
						<Match when={count() !== 1}>
							<MaterialSymbol
								symbol="groups"
								color="primary"
								relativeSize={0.85}
							/>
						</Match>
					</Switch>
					<Text family="mono" size="xsmall" color="fg3">
						Context:
					</Text>
				</div>
				<div class="question-box-creators__body">
					<Switch>
						<Match when={props.notice}>
							<Text size="xsmall" weight="semi-bold" color="fg2">
								{props.notice}
							</Text>
						</Match>
						<Match when={count() === 0}>
							<Text weight="bold" size="small">
								All subscriptions
								{props.subscriptionCount != null
									? ` (${props.subscriptionCount})`
									: ""}
							</Text>
						</Match>
						<Match when={count() <= MAX_NAMED_CREATORS}>
							<Text weight="bold" size="small">
								{props.creators.map(creatorName).join(", ")}
							</Text>
						</Match>
						<Match when={count() > MAX_NAMED_CREATORS}>
							<Text weight="bold" size="small">
								{count()} creators
							</Text>
						</Match>
					</Switch>
				</div>
			</div>
			<HStack align="center" spacing="medium">
				<Show when={props.detail}>
					<Text
						class="question-box-creators__detail"
						family="mono"
						size="xsmall"
						color="fg4"
					>
						{props.detail}
					</Text>
				</Show>
				{props.trailing}
			</HStack>
		</HStack>
	);
};
